import {
  FC,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { APP_ROUTES } from '@navigation/types';
import { useUnauthorizedModal } from '@hooks/useUnauthorizedModal';
import { useScreenSizes } from '@hooks/useScreenSizes';
import { useAuthUser } from '@redux/selectors/auth';
import { useUserDataById } from '@redux/selectors/users';
import theme from 'theme';
import { ParticipantPosition, ParticipantRole } from '@typings/participant';
import { PARTICIPANT_SKEW_OFFSET } from '@utils/constants';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import UserScore from '@components/userScore';
import ImagePlaceholder from '@components/loader/ImagePlaceholder';
import ImageWithBackground from '@components/image/ImageWithBackground';
import defaultBackground from '@assets/images/background.png';
import defaultAvatar from '@assets/images/avatar.png';

import HostLabel from './HostLabel';

const Container = styled(TouchableOpacity).attrs<{
  $isLeft?: boolean;
  $width: number;
  $height: number;
  $skewOffset: number;
  $background: string;
}>(({ $width, $skewOffset, $height, $isLeft, $background }) => ({
  style: {
    width: `${$width + $skewOffset}px`,
    height: `${$height}px`,
    backgroundImage: `url(${$background})`,
    clipPath: $isLeft
      ? `polygon(
    0 0,
    ${$width + $skewOffset}px 0,
    ${$width}px ${$height}px,
    0 ${$height}px
  )`
      : `polygon(${$skewOffset}px 0, ${$width + $skewOffset}px 0, ${$width + $skewOffset}px ${$height}px, 0 ${$height}px)`,
  },
}))<{
  $isLeft?: boolean;
  $width: number;
  $height: number;
  $skewOffset: number;
  $background: string;
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  border: none;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  z-index: 1;
`;

const AvatarImage = styled.img<{ $isLeft?: boolean; $isLoaded?: boolean }>`
  position: absolute;
  bottom: 0;
  height: 80%;
  object-fit: cover;
  align-self: flex-end;
  scale: 1.5;
  z-index: 1;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    scale: 1.3;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    scale: 1.25;
    left: ${({ $isLeft }) => ($isLeft ? '0' : 'auto')};
    right: ${({ $isLeft }) => ($isLeft ? 'auto' : '0')};
  }
`;

const BackgroundImage = styled.img`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const StyledPlaceholder = styled(ImagePlaceholder)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const HostLabelWrapper = styled.div.attrs<{
  $isLeft?: boolean;
  $skewOffset: number;
}>(({ $isLeft, $skewOffset }) => ({
  style: {
    left: $isLeft ? undefined : `${$skewOffset}px`,
  },
}))<{
  $isLeft?: boolean;
  $skewOffset: number;
}>`
  position: absolute;
  top: 8px;
  ${({ $isLeft }) => ($isLeft ? 'right: 16px;' : '')}
  padding-inline: 16px;
`;

const UserScoreWrapper = styled(UserScore)<{
  $isLeft?: boolean;
  $height: number;
}>`
  position: absolute;
  bottom: ${({ $height }) => $height * 0.4}px;
  ${({ $isLeft }) => ($isLeft ? 'left: 0;' : 'right: 4px;')}
`;

type Props = {
  position: ParticipantPosition;
  userId?: string;
  height: number;
  width: number;
  participantRole?: ParticipantRole;
  isScaling?: boolean;
};

const SkewedBackgroundAvatar: FC<Props> = ({
  position,
  userId,
  height,
  width,
  participantRole,
  // isScaling,
}) => {
  const { isAnonymous } = useAuthUser();
  const userData = useUserDataById(userId);
  const [avatar, setAvatar] = useState<string>(() =>
    userData?.image ? userData.image : defaultAvatar,
  );
  const [background, setBackground] = useState<string>(() =>
    userData?.background ? userData.background : defaultBackground,
  );
  const { isMobile, isTablet } = useScreenSizes();
  const { openUnauthorizedModal } = useUnauthorizedModal();
  const navigate = useNavigate();

  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth === 0) {
      setAvatar(defaultAvatar);
    }
  }, []);

  const onImageError = useCallback(() => {
    setAvatar(defaultAvatar);
  }, []);

  const onBackgroundLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      if (e.currentTarget.naturalWidth === 0) {
        setBackground(defaultBackground);
      }
    },
    [],
  );

  const onBackgroundError = useCallback(() => {
    setBackground(defaultBackground);
  }, []);

  const onAvatarClick = useCallback(() => {
    if (isAnonymous) {
      return openUnauthorizedModal();
    }

    if (userData?.id) {
      navigate(`${APP_ROUTES.PROFILE}/${userData.id}`);
    }
  }, [userData?.id, navigate, openUnauthorizedModal, isAnonymous]);

  useEffect(() => {
    setAvatar(userData?.image || defaultAvatar);
  }, [userData?.image]);

  useEffect(() => {
    setBackground(userData?.background || defaultBackground);
  }, [userData?.background]);

  return (
    <Container
      $isLeft={position === ParticipantPosition.Left}
      onClick={onAvatarClick}
      $width={width}
      $height={height}
      $skewOffset={Math.ceil(height * PARTICIPANT_SKEW_OFFSET)}
      $background={background}
      disabled={isAnonymous}
    >
      <ImageWithBackground
        image={avatar}
        background={background}
        onImageLoad={onImageLoad}
        onImageError={onImageError}
        onBackgroundLoad={onBackgroundLoad}
        onBackgroundError={onBackgroundError}
        BackgroundComponent={BackgroundImage}
        ImageComponent={AvatarImage}
        PlaceholderComponent={StyledPlaceholder}
        imageProps={{ $isLeft: position === ParticipantPosition.Left }}
      />

      {!!participantRole && (
        <HostLabelWrapper
          $isLeft={position === ParticipantPosition.Left}
          $skewOffset={Math.ceil(height * PARTICIPANT_SKEW_OFFSET)}
        >
          <HostLabel role={participantRole} />
        </HostLabelWrapper>
      )}

      {!!userId && (
        <UserScoreWrapper
          userId={userId}
          value={userData?.score}
          size={
            isMobile || isTablet
              ? theme.fonts.fontSize['3xl']
              : theme.fonts.fontSize['5xl']
          }
          $isLeft={position === ParticipantPosition.Left}
          $height={height}
        />
      )}
    </Container>
  );
};

export default memo(SkewedBackgroundAvatar);
